import React, { useState, useMemo, useEffect } from 'react';
import { Sprout, X as XIcon, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

// Family → helpful grouping tip. When a user has 5+ plants sharing a
// family, we surface one of these on the library page as a friendly
// nudge that they could give their collection a shared care routine.
// Keep the copy specific + actionable — no generic "you have X plants
// filler." Fallback for unknown families is the generic line below.
const FAMILY_TIPS = {
  Araceae: 'love higher humidity — a pebble tray or grouping near a humidifier gives them a shared microclimate',
  Cactaceae: 'thrive in bright light and gritty soil — a south-facing window shelf can host them all',
  Crassulaceae: 'succulents prefer dry-between-waterings — grouping them makes the "check on Sundays" routine easy',
  Bromeliaceae: 'like water pooled in their central cup — refresh weekly to prevent stagnation',
  Asparagaceae: 'tolerate low light and irregular watering — perfect for busy corners you sometimes forget',
  Orchidaceae: 'need airflow + cyclical dryness — grouping them helps you sync watering days',
  Gesneriaceae: 'appreciate warmth and humidity — a bathroom or kitchen shelf suits the whole family',
  Piperaceae: 'peperomias love bright indirect light and moderate humidity — a filtered window works for all of them',
  Moraceae: 'ficus dislike being moved — keep them together in one stable, well-lit spot',
  Marantaceae: 'prayer plants thrive in humidity + soft light — grouping them creates a humid microclimate',
  Polypodiaceae: 'ferns love consistent moisture — clustered together they hold humidity as a mini-jungle',
  Begoniaceae: 'begonias like humidity but hate wet leaves — bottom-water them together',
  Euphorbiaceae: 'many act like succulents — group them with bright-light lovers on a sunny shelf',
  Rubiaceae: 'coffee family plants like moderate humidity + steady water — a group makes routine care easier',
  Musaceae: 'bananas love heat, humidity, and space — cluster them near a bright warm window',
  Zingiberaceae: 'gingers love heat + humidity — a humid corner cluster suits them all',
  Amaryllidaceae: 'many need a cool dormancy period — grouping them helps you remember the seasonal rhythm',
  Apocynaceae: 'many are drought-tolerant succulents — treat as cacti and group with other bright-light lovers',
  Asteraceae: 'daisy family — most love bright direct light and moderate water',
  Lamiaceae: 'mint family plants love sun + regular pinching — group them so trimming day is one task, not five',
};

const FAMILY_TIP_MIN = 5; // minimum count to surface the tip

// Snooze options offered on the split-button. 1/3/7/30d matches the
// user's spec — same cadence as the duplicate-banner snooze.
const SNOOZE_OPTIONS = [
  { days: 1, key: 'aiTipSnooze1d', fallback: 'Hide for 1 day' },
  { days: 3, key: 'aiTipSnooze3d', fallback: 'Hide for 3 days' },
  { days: 7, key: 'aiTipSnooze7d', fallback: 'Hide for 7 days' },
  { days: 30, key: 'aiTipSnooze30d', fallback: 'Hide for 30 days' },
];

// localStorage keys.
const PER_FAMILY_SNOOZE_PREFIX = 'ai_tip_snoozed_until_';
const ALL_SNOOZE_KEY = 'ai_tips_all_snoozed_until';

const readSnoozeTs = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const ts = Number(raw);
    return Number.isFinite(ts) && ts > Date.now() ? ts : 0;
  } catch { return 0; }
};

/**
 * When a user has 5+ plants sharing the same botanical family, surface
 * a small tip suggesting they group them for shared care. Dismissible
 * per-family (survives across sessions via localStorage). Skips families
 * we don't have a specific tip for.
 *
 * Sits alongside the other library banners (backfill prompt, local-mode
 * warning). One tip visible at a time — the family with the highest
 * plant count that hasn't been dismissed wins.
 *
 * Props:
 *   • plants — the full plant array (unfiltered).
 *   • onFilterFamily(family) — optional. Called when the user taps the
 *     banner body or the "View plants" chip. Filters the library to
 *     that family so the user can see who was referenced.
 */
export const FamilyGroupingTip = ({ plants, onFilterFamily }) => {
  const { t } = useLanguage();
  // Permanent per-family dismissals (legacy "×" button behaviour).
  const [dismissed, setDismissed] = useState(() => {
    const s = new Set();
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('family_tip_dismissed_')) {
          const fam = key.slice('family_tip_dismissed_'.length);
          if (fam) s.add(fam);
        }
      }
    } catch (_e) { /* SSR / privacy mode */ }
    return s;
  });

  // Per-family snoozes (timestamp-based, short-lived). Read once on
  // mount, refreshed hourly so an expired snooze re-surfaces without
  // needing a page reload.
  const [snoozeVersion, setSnoozeVersion] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSnoozeVersion(v => v + 1), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Global snooze — hides every AI suggestion for N days. Set from the
  // "Hide all suggestions" option in the split-button menu.
  const allSnoozedUntil = useMemo(() => readSnoozeTs(ALL_SNOOZE_KEY), [snoozeVersion]);

  // Compute the family with the highest non-dismissed and non-snoozed
  // plant count that meets the FAMILY_TIP_MIN threshold.
  const winner = useMemo(() => {
    if (!plants || plants.length < FAMILY_TIP_MIN) return null;
    if (allSnoozedUntil) return null;
    const counts = {};
    for (const p of plants) {
      const fam = (p?.care_details?.family || '').trim();
      if (!fam) continue;
      if (dismissed.has(fam)) continue;
      if (readSnoozeTs(`${PER_FAMILY_SNOOZE_PREFIX}${fam}`)) continue;
      counts[fam] = (counts[fam] || 0) + 1;
    }
    let bestFam = null;
    let bestCount = 0;
    for (const [fam, c] of Object.entries(counts)) {
      if (c >= FAMILY_TIP_MIN && c > bestCount) {
        bestFam = fam;
        bestCount = c;
      }
    }
    if (!bestFam) return null;
    return { family: bestFam, count: bestCount };
  }, [plants, dismissed, allSnoozedUntil, snoozeVersion]);

  if (!winner) return null;

  const tipText = FAMILY_TIPS[winner.family]
    || 'sharing a family means they share care preferences — grouping them makes your routine easier';

  const handleDismiss = () => {
    try {
      localStorage.setItem(`family_tip_dismissed_${winner.family}`, '1');
    } catch (_e) { /* localStorage quota; ignore */ }
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(winner.family);
      return next;
    });
  };

  const snoozeFamily = (days) => {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    try { localStorage.setItem(`${PER_FAMILY_SNOOZE_PREFIX}${winner.family}`, String(until)); } catch { /* quota */ }
    setSnoozeVersion(v => v + 1);
    toast.success(
      (t('aiTipSnoozeToast') || 'Suggestion hidden for {n} days').replace('{n}', days)
    );
  };

  const snoozeAll = (days) => {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    try { localStorage.setItem(ALL_SNOOZE_KEY, String(until)); } catch { /* quota */ }
    setSnoozeVersion(v => v + 1);
    toast.success(
      (t('aiTipSnoozeAllToast') || 'All suggestions hidden for {n} days').replace('{n}', days)
    );
  };

  const handleFilter = () => {
    if (typeof onFilterFamily === 'function') {
      onFilterFamily(winner.family);
      toast.success(
        (t('aiTipFilterToast') || 'Filtered to {family} ({count} plants). Tap × to clear.')
          .replace('{family}', winner.family)
          .replace('{count}', winner.count)
      );
    }
  };

  const canFilter = typeof onFilterFamily === 'function';
  const bodyLabel = (t('aiTipShowFamily') || 'Show all {family} plants').replace('{family}', winner.family);

  return (
    <div
      className="mt-3 rounded-lg px-3 py-2.5"
      style={{ backgroundColor: 'hsl(82, 55%, 96%)', border: '1px solid hsl(143, 28%, 82%)' }}
      role="status"
      aria-live="polite"
      data-testid="family-grouping-tip"
    >
      <div className="flex items-start gap-2">
        <Sprout
          className="w-4 h-4 flex-shrink-0 mt-0.5"
          style={{ color: 'hsl(143, 45%, 34%)' }}
          aria-hidden="true"
        />
        {canFilter ? (
          <button
            type="button"
            onClick={handleFilter}
            className="flex-1 min-w-0 text-left text-sm leading-snug rounded transition-colors hover:bg-white/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
            style={{ color: 'hsl(143, 28%, 22%)' }}
            aria-label={bodyLabel}
            data-testid="family-grouping-tip-body"
          >
            <span className="font-medium">
              {t('familyTipLead')?.replace('{family}', winner.family).replace('{count}', winner.count)
                || `Your ${winner.family} collection (${winner.count})`}
            </span>{' '}
            <span>{tipText}</span>
            <span
              className="ml-1 whitespace-nowrap text-xs font-medium underline"
              style={{ color: 'hsl(143, 45%, 34%)' }}
            >
              {t('aiTipViewPlants') || 'View plants'}
            </span>
          </button>
        ) : (
          <p className="flex-1 min-w-0 text-sm leading-snug" style={{ color: 'hsl(143, 28%, 22%)' }}>
            <span className="font-medium">
              {t('familyTipLead')?.replace('{family}', winner.family).replace('{count}', winner.count)
                || `Your ${winner.family} collection (${winner.count})`}
            </span>{' '}
            <span>{tipText}</span>
          </p>
        )}
        <div
          className="flex-shrink-0 inline-flex items-stretch rounded-full overflow-hidden"
          style={{ border: '1px solid hsl(143, 28%, 70%)', backgroundColor: 'white' }}
          data-testid="ai-tip-snooze-group"
        >
          <button
            type="button"
            onClick={() => snoozeFamily(7)}
            className="text-[11px] px-2.5 py-1 transition-colors hover:opacity-80"
            style={{ color: 'hsl(143, 28%, 25%)' }}
            data-testid="ai-tip-snooze"
            title={t('aiTipSnoozeTip') || 'Hide this suggestion for 7 days'}
          >
            {t('aiTipSnooze7d') || 'Hide for 7 days'}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="px-1.5 py-1 transition-colors hover:opacity-80 inline-flex items-center"
                style={{ color: 'hsl(143, 28%, 25%)', borderLeft: '1px solid hsl(143, 28%, 82%)' }}
                aria-label={t('dupeSnoozeMore') || 'More snooze options'}
                data-testid="ai-tip-snooze-more"
              >
                <ChevronDown className="w-3 h-3" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="ai-tip-snooze-menu">
              <DropdownMenuLabel>{winner.family}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {SNOOZE_OPTIONS.map(({ days, key, fallback }) => (
                <DropdownMenuItem
                  key={`one-${days}`}
                  onClick={() => snoozeFamily(days)}
                  data-testid={`ai-tip-snooze-${days}d`}
                >
                  {t(key) || fallback}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>
                {t('aiTipSnoozeAll')?.replace('{n}', '')?.trim() || 'Hide all suggestions'}
              </DropdownMenuLabel>
              {SNOOZE_OPTIONS.map(({ days }) => (
                <DropdownMenuItem
                  key={`all-${days}`}
                  onClick={() => snoozeAll(days)}
                  data-testid={`ai-tip-snooze-all-${days}d`}
                >
                  {(t('aiTipSnoozeAll') || 'Hide all suggestions for {n} days').replace('{n}', days)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex-shrink-0 p-0.5 rounded hover:bg-white/60 transition-colors"
          style={{ color: 'hsl(143, 28%, 45%)' }}
          aria-label={t('dismiss') || 'Dismiss'}
          data-testid="family-grouping-tip-dismiss"
        >
          <XIcon className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default FamilyGroupingTip;
